import { CommonModule, NgFor, NgIf } from '@angular/common';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

import { Store } from '@ngrx/store';

import { ListsAddEditModalData } from '@components/orders/saved-lists/lists-add-edit-modal/lists-add-edit-modal.component';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import { FiltersAndPaginationPayloadService } from '../../../../@core/services/filters-and-pagination-payload.service';
import { OrdersActions } from '../../../../@store/orders/orders.actions';
import { OrderClient, SingleList } from '../../../../@store/orders/orders.model';
import {
  selectCustomerCodes,
  selectOrderAssignees,
  selectOrderStatuses,
  selectSingleList,
} from '../../../../@store/orders/orders.selectors';
import { QuotesActions } from '../../../../@store/quotes/quotes.actions';
import { selectCustomers } from '../../../../@store/quotes/quotes.selectors';
import { Customer, QuotePerson } from '../../../../@store/quotes/quotes.state.interface';
import { CONFIG } from '../../../../config';

@Component({
  selector: 'ciwc-manage-saved-lists',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    NgFor,
    MatAutocomplete,
    MatInput,
    MatAutocompleteTrigger,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    NgIf,
    MatProgressSpinnerModule,
  ],
  providers: [DatePipe],
  standalone: true,
  templateUrl: './manage-saved-lists.component.html',
  styleUrl: './manage-saved-lists.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageSavedListsComponent implements OnInit, OnDestroy {
  @Input() listId!: number | undefined;
  @Input() data!: ListsAddEditModalData | null;

  @Output() saveList = new EventEmitter<{ id: number | undefined; payload: any }>();
  private readonly store = inject(Store);
  private readonly fb = inject(FormBuilder);
  private readonly payloadService = inject(FiltersAndPaginationPayloadService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly datePipe = inject(DatePipe);

  readonly orderStatuses = this.store.selectSignal(selectOrderStatuses);
  readonly assignees = this.store.selectSignal(selectOrderAssignees);
  readonly orderCustomers = this.store.selectSignal(selectCustomers);
  readonly customerCodes = this.store.selectSignal(selectCustomerCodes);
  readonly singleList = this.store.selectSignal(selectSingleList);

  private customerFilter = signal<string>('');
  filteredCustomer: Customer[] = [];
  filteredCustomerCodes: string[] = [];

  form: FormGroup;

  readonly allLoaded = computed(() => {
    const statuses = this.orderStatuses();
    const assignees = this.assignees();
    const customers = this.orderCustomers();
    const codes = this.customerCodes();
    // If editing, require singleList to be loaded
    if (this.listId) {
      const list = this.singleList();
      return (
        !!list &&
        Array.isArray(statuses) &&
        statuses.length > 0 &&
        Array.isArray(assignees) &&
        assignees.length > 0 &&
        Array.isArray(customers) &&
        customers.length > 0 &&
        Array.isArray(codes) &&
        codes.length > 0
      );
    }
    // If adding, just require the other data
    return (
      Array.isArray(statuses) &&
      statuses.length > 0 &&
      Array.isArray(assignees) &&
      assignees.length > 0 &&
      Array.isArray(customers) &&
      customers.length > 0 &&
      Array.isArray(codes) &&
      codes.length > 0
    );
  });

  constructor() {
    this.form = this.fb.group({
      status: [null],
      assignee: [null],
      customer: [null],
      customerCode: [''],
      listName: ['', []],
      fromDate: [null],
      toDate: [null],
      createdDateFrom: [null],
      createdDateTo: [null],
      jacketMaterial: [''],
      expeditedOrder: [false],
      faiReport: [false],
      materialCIW: [false],
      materialCFM: [false],
    });

    // Patch form only when allLoaded is true and in edit mode
    effect(() => {
      if (this.allLoaded()) {
        if (this.listId) {
          // Edit mode - patch with API data
          const list = this.singleList()!;
          const statuses = this.orderStatuses()!;
          const assignees = this.assignees()!;
          const customers = this.orderCustomers()!;
          if (list) {
            this.patchForm(list, statuses, assignees, customers);
          }
        } else if (this.data?.filtersForm) {
          this.addPatchWithFilters();
        }
      }
    });

    // Effect for customers filtering
    effect(() => {
      const customers = this.orderCustomers();
      const filterValue = this.customerFilter().toLowerCase();
      if (customers) {
        this.filteredCustomer = filterValue
          ? customers.filter(
              (customers) =>
                customers.firstName.toLowerCase().includes(filterValue) ||
                customers.company.toLowerCase().includes(filterValue) ||
                customers.email.toLowerCase().includes(filterValue),
            )
          : customers;
      }
    });

    // Simple valueChanges for customerCodes with debug logs
    this.form.get('customerCode')!.valueChanges.subscribe((input: string) => {
      const codes = this.customerCodes();

      if (codes) {
        this.filteredCustomerCodes = input
          ? codes.filter((code) => code.toLowerCase().includes(input.toLowerCase()))
          : codes;
      } else {
        this.filteredCustomerCodes = [];
      }
    });
  }

  private addPatchWithFilters() {
    // Add mode - patch with filter values
    const statuses = this.orderStatuses()!;
    const assignees = this.assignees()!;

    // Find status object by value
    const statusObj = statuses.find((s) => s.value === this.data?.filtersForm?.statusFilter);

    // Find assignee object by id
    const assigneeObj = assignees.find(
      (a) => a.id === Number(this.data?.filtersForm?.assigneeFilter),
    );

    this.form.patchValue({
      status: statusObj?.key || null,
      assignee: assigneeObj?.id || null,
      fromDate: this.data?.filtersForm?.fromDate,
      toDate: this.data?.filtersForm?.toDate,
      // Don't patch other fields as they should be empty in add mode
    });
  }

  /**
   * Patches the form with the provided list data, statuses, assignees, and customers.
   * @param list
   * @param statuses
   * @param assignees
   * @param customers
   */
  private patchForm(
    list: SingleList,
    statuses: any[],
    assignees: QuotePerson[],
    customers: Customer[],
  ) {
    // Status
    let statusKey = null;
    if (list.statusFilter) {
      const statusObj = statuses.find(
        (s) =>
          s.value.trim().toLowerCase() === list.statusFilter!.trim().toLowerCase() ||
          s.key.trim().toLowerCase() === list.statusFilter!.trim().toLowerCase(),
      );
      statusKey = statusObj ? statusObj.key : null;
    }
    // Assignee
    let assigneeId = null;
    const assigneeIdValue = list.assignee?.id ?? null;
    if (assigneeIdValue !== null) {
      const assigneeObj = assignees.find((a) => a.id === Number(assigneeIdValue));
      assigneeId = assigneeObj ? assigneeObj.id : null;
    }
    // Client
    let customerObj = null;
    if (list.customer && typeof list.customer === 'object' && list.customer.id) {
      customerObj = customers.find((c) => c.id === Number(list.customer.id)) || null;
    }

    this.form.patchValue(
      {
        listName: list.listName,
        fromDate: list.fromDate ? new Date(list.fromDate) : null,
        toDate: list.toDate ? new Date(list.toDate) : null,
        createdDateFrom: list.createdFromDate ? new Date(list.createdFromDate) : null,
        createdDateTo: list.createdToDate ? new Date(list.createdToDate) : null,
        assignee: assigneeId,
        status: statusKey,
        customer: customerObj,
        customerCode: list.customer?.customerCode ?? '',
        jacketMaterial: list.jacketMaterial,
        expeditedOrder: !!list.expeditedOrder,
        faiReport: !!list.faiReport,
        materialCIW: !!list.materialCIW,
        materialCFM: !!list.materialCFM,
      },
      { emitEvent: false },
    );

    if (
      list.customer?.customerCode &&
      !this.filteredCustomerCodes.includes(list.customer.customerCode)
    ) {
      this.filteredCustomerCodes = [list.customer.customerCode, ...this.filteredCustomerCodes];
    }

    // After patching, make the API call with all values
    // this.dispatchLoadOrdersWithCurrentForm();
  }

  private dispatchLoadOrdersWithCurrentForm(): void {
    const formValue = this.form.value;
    const payload: any = {};
    // Status
    if (formValue.status) payload.status = formValue.status;
    // Assignee
    if (formValue.assignee) payload.assignee = formValue.assignee;
    // Client
    if (formValue.client?.id) payload.client = formValue.client.id;
    // Customer code
    if (formValue.customerCode) payload.customerCode = formValue.customerCode;
    // Due dates
    const fromDate = formValue.fromDate
      ? this.datePipe.transform(formValue.fromDate, CONFIG.date.formatUS)
      : null;
    const toDate = formValue.toDate
      ? this.datePipe.transform(formValue.toDate, CONFIG.date.formatUS)
      : null;
    if (fromDate) payload.fromDate = fromDate;
    if (toDate) payload.toDate = toDate;
    // Created dates
    const createdDateFrom = formValue.createdDateFrom
      ? this.datePipe.transform(formValue.createdDateFrom, CONFIG.date.formatUS)
      : null;
    const createdToDate = formValue.createdDateTo
      ? this.datePipe.transform(formValue.createdDateTo, CONFIG.date.formatUS)
      : null;
    if (createdDateFrom) payload.fromDateCreated = createdDateFrom;
    if (createdToDate) payload.toDateCreated = createdToDate;
    // Boolean filters
    ['expeditedOrder', 'faiReport', 'materialCIW', 'materialCFM'].forEach((field) => {
      if (formValue[field]) payload[field] = formValue[field];
    });
    // Jacket material
    if (formValue.jacketMaterial) payload.jacketMaterial = formValue.jacketMaterial;
    // Update payload service with all initial values at once
    this.payloadService.updateFilterPayload(payload);
    this.dispatchLoadOrders();
  }

  ngOnInit() {
    if (this.listId) {
      this.store.dispatch(OrdersActions.loadSingleList({ id: this.listId }));
    }
    // Load assignees, statuses, and customers from the store
    this.store.dispatch(OrdersActions.loadOrderAssignees({ payload: { category: 'ORDERS' } }));
    this.store.dispatch(
      OrdersActions.loadOrderStatuses({ payload: { identifier: 'ORDERS_STATUS' } }),
    );
    this.store.dispatch(QuotesActions.loadCustomers());
    this.store.dispatch(OrdersActions.loadCustomerCodes({ payload: { limit: -1 } }));

    // Disable filter subscriptions for now
    // this.setupFilterSubscriptions();
  }

  private setupFilterSubscriptions(): void {
    // Status filter
    this.form
      .get('status')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((status) => {
        if (status === null) return;
        this.payloadService.updateFilterPayload({ status });
        this.dispatchLoadOrders();
      });

    // Assignee filter
    this.form
      .get('assignee')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((assignee) => {
        if (assignee === null) return;
        this.payloadService.updateFilterPayload({ assignee });
        this.dispatchLoadOrders();
      });

    // Client filter
    this.form
      .get('customer')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        filter((customer) => customer !== null && typeof customer === 'object'),
      )
      .subscribe((customer) => {
        if (!customer) return;
        this.payloadService.updateFilterPayload({ customer: customer.id });
        this.dispatchLoadOrders();
      });

    // Customer code filter
    this.form
      .get('customerCode')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((code) => {
        if (!code) return;
        this.payloadService.updateFilterPayload({ customerCode: code });
        this.dispatchLoadOrders();
      });

    // Date filters
    this.form
      .get('fromDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(() => this.handleDateFilters());

    this.form
      .get('toDate')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(() => this.handleDateFilters());

    // Created date filters
    this.form
      .get('createdDateFrom')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(() => this.handleCreatedDateFilters());

    this.form
      .get('createdDateTo')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(() => this.handleCreatedDateFilters());

    // Boolean filters
    ['expeditedOrder', 'faiReport', 'materialCIW', 'materialCFM'].forEach((field) => {
      this.form
        .get(field)
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
        .subscribe((value) => {
          this.payloadService.updateFilterPayload({ [field]: value });
          this.dispatchLoadOrders();
        });
    });

    // Jacket material filter
    this.form
      .get('jacketMaterial')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((material) => {
        if (!material) return;
        this.payloadService.updateFilterPayload({ jacketMaterial: material });
        this.dispatchLoadOrders();
      });
  }

  private handleDateFilters(): void {
    const fromDate = this.form.get('fromDate')?.value
      ? this.datePipe.transform(this.form.get('fromDate')?.value, CONFIG.date.formatUS)
      : null;
    const toDate = this.form.get('toDate')?.value
      ? this.datePipe.transform(this.form.get('toDate')?.value, CONFIG.date.formatUS)
      : null;

    if (fromDate || toDate) {
      this.payloadService.updateFilterPayload({
        fromDate,
        toDate,
      });
      this.dispatchLoadOrders();
    }
  }

  private handleCreatedDateFilters(): void {
    const fromDate = this.form.get('createdDateFrom')?.value
      ? this.datePipe.transform(this.form.get('createdDateFrom')?.value, CONFIG.date.formatUS)
      : null;
    const toDate = this.form.get('createdDateTo')?.value
      ? this.datePipe.transform(this.form.get('createdDateTo')?.value, CONFIG.date.formatUS)
      : null;

    if (fromDate || toDate) {
      this.payloadService.updateFilterPayload({
        fromDateCreated: fromDate,
        toDateCreated: toDate,
      });
      this.dispatchLoadOrders();
    }
  }

  private dispatchLoadOrders(): void {
    this.store.dispatch(
      OrdersActions.loadOrdersList({
        payload: this.payloadService.combinedPayload,
      }),
    );
  }

  onCustomerInput(value: string) {
    this.customerFilter.set(value);
    if (!value) {
      this.form.get('client')?.setValue(null);
    }
  }

  displayCustomer = (customer: any) => {
    if (!customer) return '';
    if (typeof customer === 'number' || typeof customer === 'string') {
      const customers = this.orderCustomers();
      const found = customers ? customers.find((c) => c.id === customer) : null;
      return found ? `${found.firstName} ${found.lastName}, ${found.company}` : '';
    }
    return `${customer.firstName} ${customer.lastName}`;
  };

  onCustomerSelected(event: any) {
    const customerObj = this.filteredCustomer.find((c) => c.id === event.option.value.id);

    this.form.get('client')?.setValue(customerObj);
  }

  displayCustomerCode = (code: string) => code || '';

  onCustomerCodesSelected(event: any) {
    this.form.get('customerCode')?.setValue(event.option.value);
  }

  onCustomerCodesFocus() {
    const codes = this.customerCodes();
    const input = this.form.get('customerCode')?.value || '';
    if (codes && (!input || input.length === 0)) {
      this.filteredCustomerCodes = codes;
    }
    // If input is not empty, do nothing (keep filtered list)
  }

  trackByStatus = (_: number, item: any) => item.key;
  trackByAssignee = (_: number, item: QuotePerson) => item.id;
  trackByClient = (_: number, item: OrderClient) => item.id;
  trackByCustomerCode = (_: number, item: string) => item;

  private formatDate(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  public getPayload() {
    const formValue = this.form.value;

    return {
      ...formValue,
      dueDateFrom: this.formatDate(formValue.fromDate),
      dueDateTo: this.formatDate(formValue.toDate),
      createdDateFrom: this.formatDate(formValue.createdDateFrom),
      createdDateTo: this.formatDate(formValue.createdDateTo),
      customer: formValue.customer?.id,
    };
  }

  public submit() {
    this.saveList.emit({ id: this.listId, payload: this.getPayload() });
  }

  ngOnDestroy() {
    this.payloadService.resetAll();
  }
}
